/**
 * Which mesh instances are gaussian splat draws.
 *
 * The engine draws splats through mesh instances it creates itself:
 * - a gsplat component with `unified: false` draws through its GSplatInstance, and the engine
 *   points the instance's mesh instance back at it (`meshInstance.gsplatInstance`). Its material
 *   can be the caller's own (the splat editor passes one);
 * - unified splats (the default) are drawn by the gsplat director. For each camera and layer it
 *   keeps a manager, plus a shadow-only manager when the drawn and the casting splats differ.
 *   The manager's renderer owns the draw: the quad renderer (WebGL2 and WebGPU raster) or the
 *   compute renderer (WebGPU) with its picking draw. On WebGPU the manager's shadow renderer adds
 *   one shadow-only draw per casting light.
 *
 * They are recognised by those owners, not by their materials. Material defines changed meaning
 * between engine versions: in PlayCanvas 2.21 `Material.getDefine` returns whether a define
 * exists, so the old test `getDefine('SH_BANDS') !== undefined` held for every material, and
 * every model counted as a splat. The splat materials also vary (caller materials, chunks copied
 * from the scene's template material).
 *
 * The director rebuilds its draws during the render (a camera leaving the frame, a renderer
 * switch, a light starting to cast), so collect them right before each use.
 */
import * as pc from 'playcanvas';

/** Engine internals the walk reads; typed locally because they are not in playcanvas.d.ts. */
interface SplatRendererInternals {
    meshInstance?: pc.MeshInstance | null;
    _pickMeshInstance?: pc.MeshInstance | null;
}
interface SplatManagerInternals {
    renderer?: SplatRendererInternals | null;
    shadowRenderer?: { entries?: Map<unknown, { meshInstance?: pc.MeshInstance | null }> } | null;
}
interface SplatLayerDataInternals {
    gsplatManager?: SplatManagerInternals | null;
    gsplatManagerShadow?: SplatManagerInternals | null;
}
interface GSplatDirectorInternals {
    camerasMap?: Map<unknown, { layersMap?: Map<unknown, SplatLayerDataInternals> }>;
}

function addManagerDraws(manager: SplatManagerInternals | null | undefined, out: Set<pc.MeshInstance>): void {
    if (!manager) return;
    const renderer = manager.renderer;
    if (renderer?.meshInstance) out.add(renderer.meshInstance);
    if (renderer?._pickMeshInstance) out.add(renderer._pickMeshInstance);
    manager.shadowRenderer?.entries?.forEach((entry) => {
        if (entry.meshInstance) out.add(entry.meshInstance);
    });
}

/**
 * Every unified splat draw the engine has now, for every camera and layer (forward, shadow-only
 * and picking). Fills and returns `out` (cleared first).
 */
export function collectUnifiedSplatDraws(app: pc.AppBase, out: Set<pc.MeshInstance> = new Set()): Set<pc.MeshInstance> {
    out.clear();
    const director = (app.renderer as unknown as { gsplatDirector?: GSplatDirectorInternals | null } | undefined)?.gsplatDirector;
    director?.camerasMap?.forEach((cameraData) => cameraData.layersMap?.forEach((layerData) => {
        addManagerDraws(layerData.gsplatManager, out);
        addManagerDraws(layerData.gsplatManagerShadow, out);
    }));
    return out;
}

/**
 * A splat draw: a per-component GSplatInstance's mesh instance, or one of the unified draws
 * from `collectUnifiedSplatDraws` (collected this frame).
 */
export function isSplatMeshInstance(meshInstance: pc.MeshInstance, unifiedDraws: ReadonlySet<pc.MeshInstance>): boolean {
    return !!meshInstance.gsplatInstance || unifiedDraws.has(meshInstance);
}
